import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getMeasurementHistory, deleteMeasurement } from '../../services/metrics.service';
import { MeasurementHistoryItem } from '../../types/metrics';
import { EditMeasurementDialog } from './EditMeasurementDialog';

interface MeasurementHistoryProps {
  onDataChange?: () => void;
}

export const MeasurementHistory: React.FC<MeasurementHistoryProps> = ({ onDataChange }) => {
  const [history, setHistory] = useState<MeasurementHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const [itemToEdit, setItemToEdit] = useState<MeasurementHistoryItem | null>(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getMeasurementHistory({ limit: 100 });
      setHistory(data);
    } catch (err) {
      setError('Не вдалося завантажити історію вимірювань.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleDelete = async () => {
    if (itemToDelete === null) return;

    try {
      await deleteMeasurement(itemToDelete);
      setItemToDelete(null);
      fetchHistory();
      onDataChange?.();
    } catch (err) {
      setError('Помилка при видаленні вимірювання.');
    }
  };

  const handleEditSuccess = () => {
    setItemToEdit(null);
    fetchHistory();
    onDataChange?.();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderTable = () => {
    if (loading) {
      return <Box display="flex" justifyContent="center" sx={{ my: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
      return <Alert severity="error">{error}</Alert>;
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Тип</TableCell>
              <TableCell align="right">Значення</TableCell>
              <TableCell>Дата</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Історія вимірювань порожня.
                </TableCell>
              </TableRow>
            ) : (
              history.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.measurementType.name}</TableCell>
                  <TableCell align="right">{`${item.value} ${item.unit.symbol}`}</TableCell>
                  <TableCell>{formatDate(item.measuredAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Редагувати">
                      <IconButton onClick={() => setItemToEdit(item)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Видалити">
                      <IconButton onClick={() => setItemToDelete(item.id)} size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Історія вимірювань</Typography>
      {renderTable()}

      <Dialog
        open={itemToDelete !== null}
        onClose={() => setItemToDelete(null)}
      >
        <DialogTitle>Підтвердити видалення</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ви впевнені, що хочете видалити це вимірювання? Цю дію неможливо буде скасувати.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemToDelete(null)}>Скасувати</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Видалити
          </Button>
        </DialogActions>
      </Dialog>

      <EditMeasurementDialog
        measurement={itemToEdit}
        open={itemToEdit !== null}
        onClose={() => setItemToEdit(null)}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
};
